import '../../styles/styles.css';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

import Layout from '../../components/Layout';

import AnimateIn from '../../components/AnimateIn';
import VimeoVideo from '../../components/VimeoVideo';
import Image from '../../components/Image';
import ColumnLists from '../../components/ColumnLists';
import LinkButton from '../../components/LinkButton';
import LinkList from '../../components/LinkList';
import Quote from '../../components/Quote';
import WorkHeader from '../../components/WorkHeader';

import { getProject } from '../../config/general';
import hardcodedStaticProps from '../../lib/harcodedStaticProps';

const Dopper = ({featuredProjectsList}) => {
  const project = getProject('dopper');

  return (
    <Layout metaData={{ data: project }} featuredProjectsList={featuredProjectsList}>
      <article className="components page">

      	<WorkHeader component={{ project }} />

      	<Image
          component={{
            image: '//images.yourmajesty.co/20191120063827/Dopper_Cover.jpg'
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="a-dutch-classic">A Dutch classic</h2>
          <p>
            Dopper is a household brand in The Netherlands, famous for its iconic reusable water bottles.
          </p>
          <p>
          	Your majesty was asked to redefine Dopper’s online touch-points and create an immersive storytelling site to help spread their mission, focused around three strategic pillars.
          </p>
        </AnimateIn>

        <VimeoVideo
          className="g-row"
          component={{
            posterVideo: '373366077'
          }}
        />

        <AnimateIn className="text g-row">
				  <h2 id="1-an-evolved-brand-identity">1. An evolved brand identity</h2>
					<p>To position Dopper as a mission-led business, we extended their colorful brand by adding animation and playful digital type treatments.</p>
				</AnimateIn>

				<Image
          component={{
            image:
              '//images.yourmajesty.co/20191120064313/Dopper_Brand_Evolution.jpg',
              calculateRatio: '1920,4132'
          }}
        />

        <AnimateIn className="text g-row">
				  <h2 id="2-a-modern-shopping-experience">2. A modern shopping experience</h2>
					<p>We consolidated seven legacy systems into one unified e-commerce for B2C, B2B, and B2R.</p>
					<p>In addition to being faster and simpler to use, the new Dopper e-commerce stands out with its colorful product imagery and playful animations.</p>
				</AnimateIn>

				<VimeoVideo
          className="g-row"
          component={{
            posterVideo: '374343468'
          }}
        />

        <Image
          component={{
            image:
              '//images.yourmajesty.co/20191120065701/Dopper_Shop.jpg',
              calculateRatio: '1920,2914'
          }}
        />

        <AnimateIn className="text g-row">
				  <h2 id="3-a-scalable-marketing-platform">3. A scalable marketing platform</h2>
					<p>A flexible set of modules was created to help Dopper’s marketing team to build brand pages quickly with a wide range of styles and layouts.</p>
				</AnimateIn>

				<VimeoVideo
          className="g-row"
          component={{
            posterVideo: '373366512'
          }}
        />
				
				<Image
          component={{
            image:
              '//images.yourmajesty.co/20191120065934/Dopper_Marketing.jpg',
              calculateRatio: '1920,6024'
          }}
        />
				
				<AnimateIn className="text g-row">
				  <h2 id="making-real-impact">A splashing success</h2>
					<ul>
              					<li>21% decrease in homepage bounce-rate</li>
						<li>100% increase in website pageviews</li>
	  					<li>10% increase in organic traffic</li>
	  					<li>11% increase in avg session duration on key pages</li>
	  					<li>214% increase in engagement on <a href="https://www.dopper.com/our-mission" target="_blank" rel="noopener">Dopper’s mission</a></li>
	  					<li>7% increase in average order value</li>
	  					<li>Awarded <a href="https://www.awwwards.com/sites/dopper" target="_blank" rel="noopener">Awwwards Honorable Mention</a>.</li>
       					</ul>
				</AnimateIn>

        <Quote 
        	className="g-row"
          component={{
            quote:
              '“To make an Impact with a capital I” was our goal with this project, and Your Majesty made sure we did. Their drive to deliver excellence is something to admire.',
            quotee: '— Nick Spapens, Online Marketing Manager'
          }}
        />

        <AnimateIn className="text g-row">
				  <h2 id="about-dopper">About Dopper</h2>
					<p>
            Dopper is a certified B Corporation, and its mission is crystal clear: 
            to empower people to choose reusable over single-use water bottles to protect our world’s water sources.
          </p>
					<p>
            Dive into <a href="http://www.dopper.com" target="_blank" rel="noopener">Dopper’s Digital Flagship Store</a>.
          </p>

					<h2 id="like-what-you-see-">Like what you see?</h2>
					<p>
            Send us an email to <a href="mailto:hello@yourmajesty.co">hello@yourmajesty.co</a> to book a consultation on 
            how we can take your digital touch-points to the next level.
          </p>
	
					<h2 id="collaborators">Collaborators</h2>
					<p>
						<a href="https://juliebonnemoy.com/" target="_blank" rel="noopener">Julie Bonnemoy</a>
						<br/>
						<a href="https://hoofdfabriek.nl/" target="_blank" rel="noopener">Hoofdfabriek</a>
						<br/>
						<a href="http://cargocollective.com/realexis" target="_blank" rel="noopener">Realexis Christofides</a>
					</p>
				</AnimateIn>


				<ColumnLists
          className="g-row"
          component={{
            title: 'Services',
            columns: [
              {
                title: 'Strategy',
                items: [
                  'Brand Strategy',
                  'Collaborative Working Sessions',
                  'Communications Strategy',
                  'Content Strategy',
                  'Creative Strategy',
                  'Experience Mapping',
                  'Interviews & User Testing',
                  'Launch Strategy',
                  'Product Strategy',
                  'Prototyping & Validation',
                  'Research',
                  'Roadmapping',
                  'Technical Strategy',
                  'UX Strategy',
                  'Workshop & Facilitation'
                ]
              },

              {
                title: 'Design',
                items: [
                  '3D Animation',
                  '3D Design',
                  'Art Direction',
                  'Creative Direction',
                  'Motion Design',
                  'UI/UX Design',
                  'Visual Design'
                ]
              },

              {
                title: 'Technology',
                items: [
                  'Back-end Development',
                  'Content Management System',
                  'DevOps',
                  'eCommerce',
                  'Front-end Development',
                  'Functional Prototyping',
                  'Localization',
                  'Systems Architecture',
                  'WebGL'
                ]
              },

              {
                title: 'Delivery',
                items: [
                  'A/B Testing',
                  'Analytics & Insights',
                  'Integration Support',
                  'Launch',
                  'Monitoring',
                  'Optimization',
                  'Quality Assurance',
                  'Quantitative and Qualitative Analysis',
                  'Staff Training & Support'
                ]
              },

              {
                title: 'Content',
                items: ['Animation', 'Copywriting']
              }
            ]
          }}
        />

      </article>
    </Layout>
  );
};

export { hardcodedStaticProps as getStaticProps };

export default Dopper;
